import { useMediaQuery } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/ClearOutlined'
import ContactSupportIcon from '@material-ui/icons/ContactSupportOutlined'
import ExploreIcon from '@material-ui/icons/ExploreOutlined'
import LanguageIcon from '@material-ui/icons/LanguageOutlined'
import RecordVoiceOverIcon from '@material-ui/icons/RecordVoiceOverOutlined'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { JIRA_SUPPORT_TICKET_LINK, osLiteSupportLink } from 'constants/links'
import { addTourBanner, authSelectors } from 'ducks/auth'
import { chatDrawerActions } from 'ducks/chatDrawer'
import { drawersActions, drawersSelectors } from 'ducks/drawers'
import { viewModeSelectors } from 'ducks/viewMode'
import AdaIcon from 'layout/widgets/adaChat/AdaIcon'
import { useAdaActive } from 'layout/widgets/adaChat/useAdaActive'
import { Button, Drawer, IconButton, styled, Typography } from 'opensolar-ui'
import { useCallback, useMemo } from 'react'
import { useLocale, useTranslate } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { getCommunityLinkByLocale, getHelpCenterByLocale, getWebinarLinkByCountry } from 'util/misc'

const drawerWidth = 322

const BetaLabel = styled('h3')({
  margin: 0,
  marginLeft: 10,
  marginBottom: 5,
})

const StyledDrawer = styled(Drawer)({
  '& .OSUI-Paper-root': {
    width: drawerWidth,
    flexShrink: 0,
  },
})

const paperBorder = {
  border: '1px solid rgba(0, 0, 0, 0.12)',
  borderWidth: '1px 0 0 1px',
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  drawerPaperOrg: {
    ...paperBorder,
    width: drawerWidth,
    top: 51,
    zIndex: 1499,
  },
  drawerPaperProject: {
    ...paperBorder,
    width: drawerWidth,
    top: 100,
    zIndex: 1499,
  },
  drawerPaperProjectuX3: {
    ...paperBorder,
    width: drawerWidth,
    top: 106,
    zIndex: 1499,
  },
  drawerPaperMobile: {
    width: '100%',
    zIndex: 1499,
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    justifyContent: 'space-between',
  },
  cardContainer: {
    margin: '5px 0',
    padding: 10,
    position: 'relative',
    borderRadius: 10,
    backgroundColor: '#fff',
    width: '100%',
  },
  betaCard: {
    borderRadius: 0,
    backgroundColor: '#FFE9BA',
    margin: 0,
  },
  cardTitleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 5px',
    borderRadius: 5,
    background: '#f4f4f4',
    alignSelf: 'stretch',
  },
  adaCardTitleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '5px 8px',
    borderRadius: 5,
    background:
      'linear-gradient(270deg, rgba(23, 81, 208, 0.08) -27.75%, rgba(53, 124, 230, 0.08) 10%, rgba(83, 167, 252, 0.08) 47.75%, rgba(83, 167, 252, 0.02) 161%)',
    border: '1px solid rgba(23, 81, 208, 0.15)',
    alignSelf: 'stretch',
  },
  cardTitleSection: {
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    maxWidth: 133,
  },
  icon: {
    display: 'flex',
  },
  title: {
    fontSize: 14,
    color: 'rgb(0,0,0)',
    fontWeight: 400,
    lineHeight: '16px',
    padding: '10px 10px',
    paddingRight: 4,
  },
  adaTitle: {
    fontSize: 15,
    color: '#1751D0',
    fontWeight: 600,
    lineHeight: '18px',
    padding: '8px 12px',
    paddingRight: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '16px',
    minWidth: 133,
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    fontFamily: 'Roboto',
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 400,
    marginTop: 10,
  },
}))

interface HelpCenterItem {
  Title: string
  Content: string
  Link?: string
  Function?: () => void
  Icon: React.ReactNode
  Label: string
  Include: boolean
  Beta?: boolean
  AdaCard?: boolean
}

function getHelpCenterItems(
  isUI3Manage: boolean,
  isUI3Projects: boolean,
  isUi3Page: boolean,
  isMobile: boolean,
  isOrgVerificationBlocking: boolean,
  isAdaEnabled: boolean
) {
  const HelpCenter: HelpCenterItem[] = [
    // {
    //   Title: 'New UI Help Centre',
    //   Content: 'Discover detailed guides and tips for navigating the new Project Management pages.',
    //   Link: 'https://support.opensolar.com/hc/en-us/categories/12281970173455-Projects-Management',
    //   Icon: <MenuBookOutlined />,
    //   Label: 'Help Centre',
    //   Include: ui3Enabled && isUi3Page,
    //   Beta: true,
    // },
    // {
    //   Title: 'Take the Tour',
    //   Content:
    //     'The best way to quickly get up to speed. This 30 seconds tour will familiarise you with the new Project Management features.',
    //   Link: '',
    //   Icon: <ExploreIcon />,
    //   Label: 'Begin Tour Now',
    //   Include: isUI3Manage && !isMobile,
    //   Beta: true,
    // },
    // {
    //   Title: 'Take the Tour',
    //   Content:
    //     'The best way to quickly get up to speed. This 30 seconds tour will familiarise you with the new Project List features.',
    //   Link: '',
    //   Icon: <ExploreIcon />,
    //   Label: 'Begin Tour Now',
    //   Include: isUI3Projects && !isMobile,
    //   Beta: true,
    // },
    // {
    //   Title: 'New UI Feedback',
    //   Content:
    //     'Share your thoughts and help us improve the new project management experience. We appreciate your input!',
    //   Icon: <ExploreIcon />,
    //   Label: 'Submit Feedback',
    //   Include: ui3Enabled && isUi3Page,
    //   Beta: true,
    // },
    {
      Title: 'Ada',
      Content:
        'Chat with Ada, your AI-powered solar and battery design assistant, to get help with your questions and tasks.',
      Icon: <AdaIcon size={20} />,
      Label: 'Ask Ada',
      Include: isAdaEnabled,
      AdaCard: true,
    },
    {
      Title: 'Help Center',
      Content: 'Everything you need to know about OpenSolar, including instructional videos and step-by-step guides.',
      Link: 'https://support.opensolar.com',
      Icon: <LanguageIcon />,
      Label: 'Visit Help Centre',
      Include: true,
    },
    {
      Title: 'Contact Support',
      Content: 'Submit a ticket and our team will respond as soon as possible.',
      Icon: <ContactSupportIcon />,
      Label: 'Submit a Ticket',
      Include: true,
    },

    {
      Title: 'Weekly Webinars',
      Content:
        'Join us for our weekly webinar series, covering an OpenSolar General Overview, Advanced Design and Control Centre Deep Dive.',
      Link: 'https://www.opensolar.com/webinars',
      Icon: <RecordVoiceOverIcon />,
      Label: 'Go to Webinars',
      Include: true,
    },
    // {
    //   Title: 'Community',
    //   Content:
    //     'Chat and connect with other OpenSolar professionals around the world - questions, tips, best practices.',
    //   Link: 'https://support.opensolar.com/hc/en-us/community/topics',
    //   Icon: <GroupIcon />,
    //   Label: 'Go to Community',
    //   Include: true,
    // },
  ]

  return HelpCenter.filter((x) => !!x.Include)
}

const HelpCenterLite: HelpCenterItem[] = [
  {
    Title: 'Help Center',
    Content: 'Everything you need to know about OpenSolar, including instructional videos and step-by-step guides.',
    Link: osLiteSupportLink,
    Icon: <LanguageIcon />,
    Label: 'Visit Help Centre',
    Include: true,
  },
  {
    Title: 'Contact Support',
    Content: 'Submit a ticket and our team will respond as soon as possible.',
    Link: JIRA_SUPPORT_TICKET_LINK,
    Icon: <ContactSupportIcon />,
    Label: 'Submit a Ticket',
    Include: true,
  },
  {
    Title: 'Monthly Webinar',
    Content:
      'Get all your OpenSolar Lite questions answered in our live, monthly sessions where we will show you how create a design and generate a report in just a few clicks.',
    Link: 'https://us06web.zoom.us/webinar/register/6416781353625/WN_2K-v6-9WQYOS0jKoAia3KA',
    Icon: <RecordVoiceOverIcon />,
    Label: 'Go to Webinar',
    Include: true,
  },
]

const HelpCenterButton = ({ cardData, locale, country_iso2, isUI3Manage, isUI3Projects }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const dispatch = useDispatch()
  const isUserLite = useSelector(viewModeSelectors.isUserLite)

  return (
    <div className={`${classes.cardContainer} ${cardData.Beta ? classes.betaCard : ''}`}>
      <div>
        <div className={cardData.AdaCard ? classes.adaCardTitleWrapper : classes.cardTitleWrapper}>
          <div className={classes.cardTitleSection}>
            <div className={classes.icon}>{cardData.Icon}</div>
            <div className={cardData.AdaCard ? classes.adaTitle : classes.title}>{translate(cardData.Title)}</div>
          </div>
          <Button
            className={classes.button}
            size="small"
            variant="outlined"
            fullWidth
            onClick={() => {
              if (cardData.Function) {
                cardData.Function()
                return
              }
              if (isUserLite && cardData.Link) {
                window.open(cardData.Link, '_blank')
                return
              }
              switch (cardData.Title) {
                case 'Help Center':
                  window.open(getHelpCenterByLocale(locale), '_blank')
                  break
                case 'Take the Tour':
                  dispatch(drawersActions.setRightDrawerOpen(undefined))
                  if (isUI3Manage) {
                    dispatch(addTourBanner('manage'))
                  } else if (isUI3Projects) {
                    dispatch(addTourBanner('project_list'))
                  } else {
                    dispatch(addTourBanner('main'))
                    logAmplitudeEvent('fuji_tour_started_from_help', {})
                  }
                  break
                case 'Contact Support':
                  window.open(JIRA_SUPPORT_TICKET_LINK, '_blank')
                  break
                case 'Community':
                  window.open(getCommunityLinkByLocale(locale), '_blank')
                  break
                case 'Weekly Webinars':
                  window.open(getWebinarLinkByCountry(country_iso2), '_blank')
                  break
                case 'Ada':
                  dispatch(drawersActions.setRightDrawerOpen(undefined))
                  dispatch(chatDrawerActions.setChatDrawerOpen(true))
                  break
                default:
                  window.open(cardData.Link, '_blank')
              }
            }}
          >
            <span className={classes.label}>{translate(cardData.Label)}</span>
          </Button>
        </div>
        <div>
          <div className={classes.content}>{translate(cardData.Content)}</div>
        </div>
      </div>
    </div>
  )
}

const HelpCenterDrawer = () => {
  const classes = useStyles()
  const translate = useTranslate()
  const dispatch = useDispatch()
  const locale = useLocale()
  const country_iso2 = useSelector(authSelectors.getCurrentOrgCountry)
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('xs'))
  const isUserLite = useSelector(viewModeSelectors.isUserLite)
  const isProjectLite = useSelector(viewModeSelectors.isProjectLite)
  const location = useLocation()

  const currentOrg = useSelector(authSelectors.getCurrentOrg)

  // Determine if Org verification is blocking tours
  // This will return true if the org is beyond the grace period
  const isOrgVerificationBlocking = Boolean(currentOrg && currentOrg.is_org_verification_required_and_overdue)

  const isUi3Page = useMemo(
    () =>
      location.pathname?.includes('manage') ||
      location.pathname?.includes('energy') ||
      location.pathname.includes('/projects/view'),
    [location.pathname]
  )
  const isUI3Manage = useMemo(() => location.pathname?.includes('manage'), [location.pathname])
  const isUI3Projects = useMemo(() => location.pathname?.includes('/projects/view'), [location.pathname])
  const { active } = useAdaActive()
  const isAdaEnabled = !!active

  const HelpCenter = useMemo(
    () =>
      getHelpCenterItems(
        isUI3Manage,
        isUI3Projects,
        isUi3Page,
        isMobile,
        isOrgVerificationBlocking,
        isAdaEnabled
      ),
    [isUI3Manage, isUI3Projects, isUi3Page, isMobile, isOrgVerificationBlocking, isAdaEnabled]
  )
  const helpCenterData = useMemo(
    () => (isUserLite ? HelpCenterLite : HelpCenter),
    [isUserLite, HelpCenterLite, HelpCenter]
  )
  const betaItems = useMemo(() => helpCenterData.filter((x) => x.Beta), [helpCenterData])
  const nonBetaItems = useMemo(() => helpCenterData.filter((x) => !x.Beta), [helpCenterData])
  const helpCenterOpen = useSelector(drawersSelectors.getHelpCenterOpen)

  const drawerStyle = (loc) => {
    if (
      loc.includes('explore') ||
      loc.includes('info') ||
      loc.includes('energy') ||
      loc.includes('design') ||
      loc.includes('proposal') ||
      loc.includes('preview') ||
      loc.includes('manage') ||
      loc.includes('sld')
    )
      return true
  }

  const getDrawerStyle = (loc) => {
    switch (loc) {
      case true:
        return classes.drawerPaperProjectuX3
      default:
        return classes.drawerPaperOrg
    }
  }

  const paperClass = isMobile
    ? classes.drawerPaperMobile
    : (isUserLite && isProjectLite) || isProjectLite
      ? classes.drawerPaperOrg
      : getDrawerStyle(drawerStyle(location.pathname))

  // Skip the close if the click landed on a Help Center trigger (Support
  // button, tablet-nav help icon, etc.). Otherwise mousedown would close the
  // drawer and the subsequent click would re-open it via toggleHelpCenter.
  // Activity-panel triggers (`data-side-panel-trigger="activity"`)
  // intentionally do not match, so clicking the bell while the Help Center is
  // open still closes the Help Center.
  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement | null
      if (target?.closest('[data-side-panel-trigger="help-center"]')) return
      if (target?.closest('.HelpCenterBottomButton')) return
      dispatch(drawersActions.setRightDrawerOpen(undefined))
    },
    [dispatch]
  )

  return (
    <ClickAwayListener
      mouseEvent={helpCenterOpen ? 'onMouseDown' : false}
      touchEvent={helpCenterOpen ? 'onTouchStart' : false}
      onClickAway={handleClickAway}
    >
      <div className={classes.root}>
        <StyledDrawer
          variant="persistent"
          anchor="right"
          open={helpCenterOpen}
          PaperProps={{
            className: paperClass,
          }}
        >
          <div className={classes.drawerHeader}>
            <Typography variant="h5">{translate('Support')}</Typography>
            <IconButton onClick={() => dispatch(drawersActions.setRightDrawerOpen(undefined))}>
              <CloseIcon />
            </IconButton>
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            {!!betaItems?.length && (
              <>
                <BetaLabel>{translate('New Project Management Features')}</BetaLabel>
                {betaItems.map((cardData, index) => (
                  <HelpCenterButton
                    cardData={cardData}
                    locale={locale}
                    country_iso2={country_iso2}
                    isUI3Manage={isUI3Manage}
                    isUI3Projects={isUI3Projects}
                    key={index}
                  />
                ))}
              </>
            )}
            {nonBetaItems?.map((cardData, index) => (
              <HelpCenterButton
                cardData={cardData}
                locale={locale}
                country_iso2={country_iso2}
                isUI3Manage={isUI3Manage}
                isUI3Projects={isUI3Projects}
                key={index}
              />
            ))}
          </div>
        </StyledDrawer>
      </div>
    </ClickAwayListener>
  )
}

export default HelpCenterDrawer
